/**
 * `UNASSIGN_MASTER` step. With `futureStops:true` (default) the BE cascades
 * the unassign to every future occurrence; the move pipeline opts to
 * `false` because it coordinates the fanout itself. Uses
 * `skipInvalidate:true` on every batch.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { CseId, DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface UnassignMasterStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly cseIds: ReadonlyArray<CseId>
  /**
   * v1 hardcodes `futureStops: true` (pipelines.js / payloads). v2 surfaces
   * the knob so the move pipeline can decline propagation when it manages
   * the fanout itself.
   */
  readonly futureStops?: boolean
}

export type UnassignMasterStepValue = {
  readonly cseIds: ReadonlyArray<string>
  readonly batchCount: number
}

export type UnassignMasterStepResult = StepResult<'UNASSIGN_MASTER', UnassignMasterStepValue>

export const makeUnassignMasterStep = (
  args: UnassignMasterStepArgs,
  deps: Deps,
): PipelineStep<'UNASSIGN_MASTER', UnassignMasterStepResult> => {
  const stepId = `unassign-master:${args.routeId}:${args.dayOfWeek}`
  const futureStops = args.futureStops ?? true
  const dayOfWeek = args.dayOfWeek as ApiDayOfWeek
  return {
    id: stepId,
    phase: 'UNASSIGN_MASTER',
    label: 'Failed to unassign stops from master sequence',
    applyOrder: 200,
    run: async (_ctx) => {
      if (args.cseIds.length === 0) {
        return ok({
          phase: 'UNASSIGN_MASTER',
          stepId,
          value: { cseIds: [], batchCount: 0 },
        })
      }
      const batches = chunk(args.cseIds as readonly string[], BATCH_SIZE)
      try {
        for (const batch of batches) {
          await deps
            .unassignMaster({
              routeId: args.routeId,
              data: {
                dayOfWeek,
                futureStops,
                stops: [...batch],
              },
              skipInvalidate: true,
            })
            .unwrap()
        }
        return ok({
          phase: 'UNASSIGN_MASTER',
          stepId,
          value: { cseIds: args.cseIds.map(String), batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'UNASSIGN_MASTER',
          status,
          message,
        })
      }
    },
  }
}
